import { useEffect, useMemo, useState } from 'react';

import { Loading } from 'components/shared/Loading';
import Modal from 'components/shared/Modal/Modal';
import TableSearchHeader from 'components/Table/TableNav/TableSearchHeader';
import { createTableData } from 'components/Table/TablePicker/Table.utils';
import TablePicker from 'components/Table/TablePicker/TablePicker';
import { deviceNumberSizes } from 'config/device';
import useBouncer from 'hooks/useBouncer';
import { useInfoAndData } from 'hooks/useInfoAndData';
import { useWindowSize } from 'hooks/useWindowResize';
import { getDisplayValue, getFieldValue } from 'machines/FormMachine/FormMachine.utils';

import {
  TriggerButton,
  TriggerIcon,
  MultiImportLayout,
  ModalFooter,
  SelectedCount,
  AddButton,
} from './MultiImport.style';
import { Error } from './Search.style';

type Props = {
  item: any;
  send: any;
  draft: any;
  disabled?: boolean;
};

// Multi-import is enabled for a trigger field when the backend sets
// FIELDMULTISELECT on it.
export function isMultiImportEnabled(item: any) {
  return Boolean(item?.FIELDMULTISELECT);
}

// The FIELDNAME(s) of the detail field(s) that hold quantities for imported
// lines, configured per trigger field via the `FIELDMULTIQTY` param coming
// from the backend; supports multiple fields separated by comma.
function getQtyFieldNames(item: any): string[] {
  return String(item?.FIELDMULTIQTY || '')
    .split(',')
    .map((name: string) => name.trim())
    .filter(Boolean);
}

// Resolve the qty field config objects from the current draft (detail config),
// preserving the order defined in FIELDMULTIQTY and skipping any that are not
// present in the detail.
function getQtyFields(item: any, draft: any[]) {
  return getQtyFieldNames(item)
    .map((name: string) => draft.find((field: any) => field.FIELDNAME === name))
    .filter(Boolean);
}

function MultiImport({ item, send, draft, disabled = false }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  // qtyValues[rowId][fieldId] = value
  const [qtyValues, setQtyValues] = useState<Record<string, Record<string, string>>>({});

  // Column descriptors for the per-row quantity steppers (one per qty field).
  const qtyColumns = useMemo(
    () =>
      getQtyFields(item, draft).map((field: any) => ({
        fieldId: field.FIELDID,
        label: field.FIELDCAPTION || 'Qty',
        width: field.FIELDWIDTH || undefined,
        fieldProperty: field.FIELDPROPERTY,
        numOfDecimals: field.NUMOFDECIMAL,
      })),
    [item, draft],
  );
  const qtyEnabled = qtyColumns.length > 0;

  const windowSize = useWindowSize();
  const isTabletDown = (windowSize.width ?? 0) <= deviceNumberSizes.tablet;

  const [query, setQuery] = useState('');
  const [liveQuery, setLiveQuery] = useState('');
  const setDebouncedLiveQuery = useBouncer(setLiveQuery, 500);

  const { status, data, error, isFetching } = useInfoAndData({
    query: liveQuery,
    list: item.FIELDMULTILIST || item.FIELDLIST,
    object: item.FIELDEDITOR,
    filters: item.FIELDLISTFILTERS || '',
    enabled: isModalOpen,
  }) as any;

  // Build the same row objects the table uses (keyed by accessor + stripped ID)
  // for the CURRENT search results.
  const currentRowsById = useMemo(() => {
    const map: Record<string, any> = {};
    if (data?.data && data?.infoData) {
      createTableData(data.data, data.infoData).forEach((row: any) => {
        map[row.ID] = row;
      });
    }
    return map;
  }, [data]);

  // Accumulate row objects across searches so selections made before searching
  // again are still resolvable on confirm (a selected row may no longer be part
  // of the current, filtered result set).
  const [rowsById, setRowsById] = useState<Record<string, any>>({});
  useEffect(() => {
    if (!isModalOpen) return;
    const keys = Object.keys(currentRowsById);
    if (!keys.length) return;
    setRowsById((prev) => ({ ...prev, ...currentRowsById }));
  }, [isModalOpen, currentRowsById]);

  function closeModal() {
    setIsModalOpen(false);
    setQuery('');
    setDebouncedLiveQuery('');
    setSelectedIds({});
    setQtyValues({});
    setRowsById({});
  }

  // Typing a quantity auto-selects the row; clearing it leaves selection as is.
  function handleQtyChange(id: string, fieldId: string, value: string) {
    setQtyValues((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [fieldId]: value },
    }));
    if (value !== '' && Number(value) !== 0) {
      setSelectedIds((prev) => ({ ...prev, [id]: true }));
    }
  }

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  }

  function toggleAll(ids: string[], checked: boolean) {
    setSelectedIds((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (checked) next[id] = true;
        else delete next[id];
      });
      return next;
    });
  }

  function handleAdd() {
    const resolvedRows = { ...currentRowsById, ...rowsById };
    const selections = Object.keys(selectedIds)
      .map((id) => ({ id, rowObject: resolvedRows[id] }))
      .filter(({ rowObject }) => Boolean(rowObject))
      .map(({ id, rowObject }: any) => ({
        FIELDVALUE: getFieldValue(rowObject.ID),
        DISPLAYVALUE: getDisplayValue(item, rowObject),
        // Map of qty field id -> entered value for this row.
        QTY: qtyValues[id] || {},
      }));

    if (!selections.length) {
      closeModal();
      return;
    }

    send({
      type: 'ADD_MANY_TO_STORE',
      fieldId: item.FIELDID,
      qtyFieldIds: qtyColumns.map((column) => column.fieldId),
      draft,
      selections,
    });
    closeModal();
  }

  const selectedCount = Object.keys(selectedIds).length;

  return (
    <>
      <Modal isOpen={isModalOpen} handleClose={closeModal} fullScreen>
        <MultiImportLayout>
          <TableSearchHeader
            value={query}
            label={item.FIELDCAPTION}
            isUpdating={isFetching && status !== 'loading'}
            handleChange={(e: any) => {
              setQuery(e.target.value);
              setDebouncedLiveQuery(e.target.value);
            }}
          />
          {(() => {
            if (status === 'loading') return <Loading />;
            if (error) return <Error>{error.message}</Error>;
            return (
              <TablePicker
                type='Search'
                tableOffset={0}
                valueData={data?.data}
                keyData={data?.infoData}
                mobileType={isTabletDown ? 'list' : undefined}
                showNavActions={false}
                selectable
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
                qtyEnabled={qtyEnabled}
                qtyColumns={qtyColumns}
                qtyValues={qtyValues}
                onQtyChange={handleQtyChange}
              />
            );
          })()}
          <ModalFooter>
            <SelectedCount>{selectedCount} selected</SelectedCount>
            <AddButton disabled={selectedCount === 0} onClick={handleAdd}>
              Add ({selectedCount})
            </AddButton>
          </ModalFooter>
        </MultiImportLayout>
      </Modal>
      <TriggerButton
        type='button'
        disabled={disabled}
        title={disabled ? 'Fill in the required fields first' : undefined}
        onClick={() => {
          if (disabled) return;
          setIsModalOpen(true);
        }}
      >
        <TriggerIcon />
        Multi Import
      </TriggerButton>
    </>
  );
}

export default MultiImport;
